import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options?: SelectOption[];
  placeholder?: string;
  /** Classes for the wrapper (sizing in flex rows: `w-48`, `flex-1`, `sm:w-56`). `className` styles the visible field. */
  wrapperClassName?: string;
}

interface Item {
  value: string;
  label: string;
  disabled: boolean;
  group?: string;
}

interface Position {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

const GAP = 4;
const MAX_HEIGHT = 320;

/** Sets a <select>'s value the way a user would, so React `onChange` and react-hook-form `register` both see it. */
function commitNativeValue(select: HTMLSelectElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
  setter?.call(select, value);
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

function readItems(select: HTMLSelectElement | null): Item[] {
  if (!select) return [];
  return Array.from(select.options).map((o) => ({
    value: o.value,
    label: o.text,
    disabled: o.disabled,
    group: o.parentElement instanceof HTMLOptGroupElement ? o.parentElement.label : undefined,
  }));
}

/**
 * Custom dropdown that opens flush under the field, as wide as the field (DESIGN.md §4), instead of the OS popup
 * that macOS draws on top of the control. A hidden native <select> stays the source of truth, so the public API is
 * unchanged: `options`, `<option>` children, `value`/`defaultValue`, `onChange(e)`, `ref`/`register(...)`, `disabled`,
 * `required`, `name`, `aria-*` all behave like before.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, options, placeholder, children, wrapperClassName, disabled, id, style, ...rest },
  ref,
) {
  const nativeRef = useRef<HTMLSelectElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<Position | null>(null);
  const autoId = useId();
  const buttonId = id ?? `sel-${autoId}`;
  const listId = `${buttonId}-list`;
  const typeahead = useRef({ text: '', at: 0 });

  const setRefs = useCallback(
    (el: HTMLSelectElement | null) => {
      nativeRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );

  // Label shown in the field comes from the native select (covers controlled, uncontrolled and form resets).
  useLayoutEffect(() => {
    rerender();
  }, []);
  useEffect(() => {
    const el = nativeRef.current;
    if (!el) return;
    const sync = () => rerender();
    el.addEventListener('change', sync);
    el.addEventListener('input', sync);
    return () => {
      el.removeEventListener('change', sync);
      el.removeEventListener('input', sync);
    };
  }, []);

  // Controlled `value` wins: during render the DOM still holds the previous value (React commits after render).
  const native = nativeRef.current;
  const controlled = rest.value !== undefined && rest.value !== null;
  const currentValue = controlled ? String(rest.value) : native?.value;
  const domOptions = native ? Array.from(native.options) : [];
  const selectedIndex = currentValue === undefined ? -1 : domOptions.findIndex((o) => o.value === currentValue);
  const selectedText =
    selectedIndex >= 0
      ? domOptions[selectedIndex].text
      : (options?.find((o) => o.value === currentValue)?.label ?? (currentValue === '' || currentValue === undefined ? placeholder : undefined));
  const label = selectedText ?? placeholder ?? '';
  const isPlaceholder = (currentValue === '' || currentValue === undefined) && placeholder !== undefined;

  const place = useCallback(() => {
    const b = buttonRef.current;
    if (!b) return;
    const r = b.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - GAP - 8;
    const above = r.top - GAP - 8;
    const width = Math.max(r.width, 160);
    const left = Math.min(r.left, window.innerWidth - width - 8);
    if (below < 200 && above > below) setPos({ left, width, bottom: window.innerHeight - r.top + GAP, maxHeight: Math.min(MAX_HEIGHT, above) });
    else setPos({ left, width, top: r.bottom + GAP, maxHeight: Math.min(MAX_HEIGHT, below) });
  }, []);

  const openMenu = () => {
    if (disabled) return;
    const list = readItems(nativeRef.current);
    setItems(list);
    const current = nativeRef.current?.selectedIndex ?? -1;
    setActive(current >= 0 && !list[current]?.disabled ? current : list.findIndex((i) => !i.disabled));
    place();
    setOpen(true);
  };

  const close = (focusButton = true) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  };

  const choose = (index: number) => {
    const item = items[index];
    const el = nativeRef.current;
    if (!item || item.disabled || !el) return;
    if (el.value !== item.value) commitNativeValue(el, item.value);
    close();
    rerender();
  };

  // Outside click / scroll / resize while open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!buttonRef.current?.contains(t) && !listRef.current?.contains(t)) close(false);
    };
    const onScroll = (e: Event) => {
      if (listRef.current && e.target instanceof Node && listRef.current.contains(e.target)) return;
      place();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, place]);

  // Keep the active option in view.
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const move = (from: number, step: 1 | -1) => {
    for (let i = from + step; i >= 0 && i < items.length; i += step) if (!items[i].disabled) return i;
    return from;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive((a) => move(a, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((a) => move(a, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActive(move(-1, 1));
        break;
      case 'End':
        e.preventDefault();
        setActive(move(items.length, -1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        choose(active);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation(); // don't close the surrounding dialog
        close();
        break;
      case 'Tab':
        close(false);
        break;
      default:
        if (e.key.length === 1) {
          const now = Date.now();
          const t = typeahead.current;
          t.text = now - t.at < 700 ? t.text + e.key.toLowerCase() : e.key.toLowerCase();
          t.at = now;
          const hit = items.findIndex((i) => !i.disabled && i.label.toLowerCase().startsWith(t.text));
          if (hit >= 0) setActive(hit);
        }
    }
  };

  const invalid = rest['aria-invalid'] === true || rest['aria-invalid'] === 'true';

  return (
    <div className={cn('relative min-w-0', wrapperClassName)} style={style}>
      <select ref={setRefs} disabled={disabled} tabIndex={-1} aria-hidden className="sr-only" {...rest}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
        {children}
      </select>

      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-invalid={invalid || undefined}
        aria-label={rest['aria-label']}
        aria-describedby={rest['aria-describedby']}
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onKeyDown}
        className={cn(
          'flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-line-strong bg-surface pr-2.5 pl-3 text-left text-sm text-ink',
          'transition-colors hover:border-subtle focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none',
          'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-muted max-sm:min-h-11 max-sm:text-base',
          open && 'border-accent ring-2 ring-accent/20',
          invalid && 'border-danger focus:ring-danger/20',
          className,
        )}
      >
        <span className={cn('min-w-0 truncate', isPlaceholder && 'text-subtle')}>{label || ' '}</span>
        <ChevronDown className={cn('size-4 shrink-0 text-subtle transition-transform duration-150', open && 'rotate-180')} aria-hidden />
      </button>

      {open &&
        pos &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-labelledby={buttonId}
            onMouseDown={(e) => {
              e.preventDefault(); // keep focus on the field
              e.stopPropagation(); // outside-click handlers of popovers/dialogs must not see this
            }}
            style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
            className="anim-fade-in fixed z-[70] overflow-auto overscroll-contain rounded-md border border-line bg-surface py-1 shadow-[0_12px_32px_-12px_rgba(24,24,27,0.28)]"
          >
            {items.length === 0 && <li className="px-3 py-2 text-sm text-subtle">Không có lựa chọn</li>}
            {items.map((item, i) => {
              const selected = i === selectedIndex;
              const header = item.group && item.group !== items[i - 1]?.group;
              return (
                <ItemRow key={`${item.value}-${i}`} header={header ? item.group : undefined}>
                  <li
                    id={`${listId}-${i}`}
                    data-index={i}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={item.disabled || undefined}
                    onMouseEnter={() => !item.disabled && setActive(i)}
                    onClick={() => choose(i)}
                    className={cn(
                      'mx-1 flex cursor-pointer items-center justify-between gap-3 rounded px-2.5 py-2 text-sm text-ink max-sm:min-h-11',
                      i === active && 'bg-surface-2',
                      selected && 'font-medium text-accent',
                      item.disabled && 'cursor-not-allowed text-subtle',
                      item.group && 'pl-5',
                    )}
                  >
                    <span className="min-w-0 break-words">{item.label}</span>
                    {selected && <Check className="size-4 shrink-0" aria-hidden />}
                  </li>
                </ItemRow>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
});

function ItemRow({ header, children }: { header?: string; children: ReactNode }) {
  if (!header) return <>{children}</>;
  return (
    <>
      <li role="presentation" className="px-3 pt-2 pb-1 text-xs font-semibold text-muted">
        {header}
      </li>
      {children}
    </>
  );
}
